import React, { useEffect, useState } from 'react';
import { CalendarToday, Close as CloseIcon } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';
import { CalendarPickerView } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt';
import { getIn } from 'formik';
import moment from 'moment';

import { UseFormik } from '@/utils/Formik/types';
import { ErrorMessage } from '@/utils/Messages';

import HelperText from '@/components/HelperText/HelperText';
import { Type } from '@/components/HelperText/types';
import { ErrorIcon } from '@/components/TextField/styles';

import {
  ClearButton,
  Container,
  ContainerLabel,
  InputWrapper,
  StarSymbol,
  StyledDatePicker,
  StyledLocalizationProvider,
  StyledTextField,
} from './styles';

interface DatePickerProps {
  viewsCustom?: CalendarPickerView[];
  allowKeyboardControlCustom?: boolean;
  label?: string;
  id: string;
  dataCy?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  showDaysOutsideCurrentMonth?: boolean;
  disableCloseOnSelect?: boolean;
  className?: string;
  labelClassName?: string;
  formik?: UseFormik<any>;
  minDate?: Date;
  maxDate?: Date;
  showOnlyMonth?: Date;
  helperText?: string;
  hidden?: boolean;
  required?: boolean;
  requireSymbolPosition?: 'left' | 'right';
  hasInitialDates?: boolean;
  onChange?: (a: Date | null) => void;
  disableDate?: boolean;
  disableInput?: boolean;
  customOpen?: boolean;
  onCustomClose?: Function;
  showOnlyModalPicker?: boolean;
  onMonthChange?: Function;
  onlyAlertErrorWhenTouched?: boolean;
  disableHelperText?: boolean;
  disableRequireSymbol?: boolean;
  error?: boolean;
  isFilter?: boolean;
  onClear?: () => void;
  placeholder?: string;
  fixModalOutsideClick?: boolean;
  PopperProps?: any;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  id,
  viewsCustom,
  dataCy = '',
  disablePast,
  disableFuture,
  showDaysOutsideCurrentMonth,
  disableCloseOnSelect,
  className,
  labelClassName = 'datePickerLabel',
  formik,
  minDate,
  maxDate,
  showOnlyMonth,
  helperText,
  hidden = false,
  required = false,
  requireSymbolPosition = 'left',
  hasInitialDates = false,
  onChange = () => {},
  disableDate = false,
  disableInput = false,
  customOpen = false,
  onCustomClose,
  showOnlyModalPicker,
  onMonthChange,
  onlyAlertErrorWhenTouched = false,
  disableHelperText = false,
  disableRequireSymbol = false,
  error = false,
  isFilter = false,
  onClear,
  ...rest
}) => {
  const [onOpen, setOnOpen] = useState<boolean>(false);

  const initialValue = hasInitialDates && formik ? getIn(formik.values, id) : null;
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialValue);

  const formikValue = formik ? getIn(formik.values, id) : undefined;

  useEffect(() => {
    setSelectedDate(formikValue);
  }, [formikValue]);

  const fieldError = formik ? getIn(formik.errors, id) : undefined;
  const fieldTouched = formik ? getIn(formik.touched, id) : undefined;

  const isHelperTextError = fieldError !== '' && fieldError;
  const helperTextType = isHelperTextError ? Type.error : Type.primary;

  const handleDateChange = (newDate?: Date | undefined) => {
    if (newDate) {
      formik?.setFieldTouched(id, true, false);

      const date = moment(newDate as Date);
      const year = Number(date.format('YYYY'));
      if ((year < 1870 || Number.isNaN(year)) && newDate !== null) {
        if (fieldError !== ErrorMessage.invalidDate()) {
          formik?.setFieldError(id, ErrorMessage.invalidDate());
        }
        return;
      }

      const minDateMoment = moment(minDate);
      if (minDate && Number(date.format('YYYYMMDD')) < Number(minDateMoment.format('YYYYMMDD'))) {
        if (fieldError !== ErrorMessage.minDate(minDate)) {
          formik?.setFieldError(id, ErrorMessage.minDate(minDate));
        }
        return;
      }

      formik?.setFieldError(id, '');
      formik?.setFieldValue(id, newDate);
      setSelectedDate(newDate as Date);
      onChange(newDate);
    }
  };

  const handleOpenPicker = () => {
    if (!disableInput && !customOpen) {
      setOnOpen(true);
    }
    if (onCustomClose) {
      onCustomClose();
    }
  };

  const handleClosePicker = () => {
    setOnOpen(false);
    if (onCustomClose) {
      onCustomClose();
    }
  };

  useEffect(() => {
    if (onlyAlertErrorWhenTouched) {
      if (onOpen) {
        formik?.setFieldTouched(id, true, false);
      }
    }
  }, [onOpen]);

  const helperTextValue = () => {
    if (onlyAlertErrorWhenTouched) {
      if (!disableHelperText && fieldTouched && isHelperTextError) {
        return (
          <>
            <ErrorIcon />
            {fieldError}
          </>
        );
      }
      return null;
    }

    if (!disableHelperText && isHelperTextError) {
      return (
        <>
          <ErrorIcon />
          {fieldError}
        </>
      );
    }

    return helperText;
  };

  const minmaxDates = () => {
    if (showOnlyMonth) {
      const month = showOnlyMonth.getMonth();
      const year = showOnlyMonth.getFullYear();
      const currentDate = new Date(year, month, 1);
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const firstDate = new Date(currentYear, currentMonth - 1, 1);
      const lastDate = new Date(currentYear, currentMonth, 0);

      return { firstDate, lastDate };
    }

    return {
      firstDate: minDate,
      lastDate: maxDate,
    };
  };

  const handleClear = () => {
    formik?.setFieldValue(id, null);
    setSelectedDate(null);
    if (onChange) onChange(null);
    if (onClear) onClear();
  };

  return (
    <Container
      className="datePickerContainer"
      $showOnlyModalPicker={showOnlyModalPicker}
      hidden={hidden}
    >
      <StyledLocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
        {label && (
          <ContainerLabel>
            {!disableRequireSymbol && required && requireSymbolPosition === 'left' && (
              <StarSymbol>*</StarSymbol>
            )}
            <label htmlFor={label} className={labelClassName}>
              {label}
            </label>
            {!disableRequireSymbol && required && requireSymbolPosition === 'right' && (
              <StarSymbol>*</StarSymbol>
            )}
          </ContainerLabel>
        )}

        <StyledDatePicker
          {...rest}
          onMonthChange={onCustomClose ? () => onCustomClose() : () => {}}
          open={customOpen || onOpen}
          views={viewsCustom}
          onClose={handleClosePicker}
          onOpen={() => setOnOpen(true)}
          minDate={minmaxDates().firstDate}
          maxDate={minmaxDates().lastDate}
          value={selectedDate}
          onChange={(date: any) => handleDateChange(date)}
          showToolbar={false}
          disablePast={disablePast}
          disableFuture={disableFuture}
          showDaysOutsideCurrentMonth={showDaysOutsideCurrentMonth}
          closeOnSelect={disableCloseOnSelect}
          className={className}
          disabled={disableDate}
          renderInput={(params) => (
            <InputWrapper hasValue={!!formikValue}>
              <StyledTextField
                {...params}
                $showOnlyModalPicker={showOnlyModalPicker}
                margin="none"
                variant="outlined"
                size="small"
                helperText={null}
                value={selectedDate}
                onChange={disableInput ? () => {} : (date: any) => handleDateChange(date)}
                className="customStyledTextField"
                onClick={disableInput ? undefined : handleOpenPicker}
                data-cy={dataCy}
                disabled={disableInput}
                error={error}
                placeholder={rest.placeholder}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: params.InputProps?.endAdornment || (
                    <InputAdornment position="end">
                      <IconButton onClick={handleOpenPicker} edge="end">
                        <CalendarToday />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                inputProps={{
                  ...params.inputProps,
                  readOnly: true,
                }}
              />
              {isFilter && !required && !disableInput && (
                <ClearButton onClick={() => handleClear()} type="button">
                  <CloseIcon />
                </ClearButton>
              )}
            </InputWrapper>
          )}
        />
        {!!helperTextValue() && <HelperText type={helperTextType}>{helperTextValue()}</HelperText>}
      </StyledLocalizationProvider>
    </Container>
  );
};

DatePicker.defaultProps = {
  customOpen: undefined,
  showOnlyModalPicker: false,
  onCustomClose: undefined,
  viewsCustom: ['day'],
  allowKeyboardControlCustom: true,
  onMonthChange: undefined,
  showOnlyMonth: undefined,
};

export default DatePicker;
